import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, ChevronRight, MapPin, Ruler, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import NotFoundPage from './NotFoundPage';
import { ServiceIcon, servicePath } from '../components/ServiceCard';
import { GoogleMap } from '../components/GoogleMap';
import { FaqSection, Cta } from './ServiceDetailPage';
import { useServices, useRegions, localized, regionPath } from '../hooks/useServices';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DOTS = { backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '36px 36px' };
const fadeIn = (delay = 0) => ({ opacity: 0, animationFillMode: 'forwards', animationDelay: `${delay}s` });

function useRegion(slug) {
  const [region, setRegion] = useState(undefined);
  useEffect(() => {
    setRegion(undefined);
    axios.get(`${API}/regions/${slug}`).then(r => setRegion(r.data)).catch(() => setRegion(null));
  }, [slug]);
  return region;
}

function Hero({ region }) {
  return (
    <section className="relative bg-[#0F172A] overflow-hidden" data-testid="region-hero">
      <div className="absolute inset-0 opacity-[0.06]" style={DOTS} />
      <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-24 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="animate-fadeInUp" style={fadeIn()}>
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-400 mb-10" data-testid="region-breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <ChevronRight className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="text-slate-200" aria-current="page">{region.name}</span>
          </nav>
          <span className="inline-flex items-center gap-2 text-[#3b8dff] text-sm font-semibold uppercase tracking-wider mb-5">
            <MapPin className="w-4 h-4" aria-hidden="true" /> Einsatzgebiet {region.name}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-6" data-testid="region-title">{region.h1}</h1>
          <p className="text-slate-300 leading-relaxed mb-10 max-w-xl" data-testid="region-intro">{region.intro}</p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact">
              <Button className="bg-[#066ce7] hover:bg-[#005bb5] text-white px-7 h-12 text-base font-semibold" data-testid="region-hero-contact">
                Unverbindlich anfragen <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/planner">
              <Button variant="outline" className="border-2 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white px-7 h-12 text-base font-semibold" data-testid="region-hero-planner">
                Halle konfigurieren
              </Button>
            </Link>
          </div>
        </div>
        <div className="relative animate-fadeInUp" style={fadeIn(0.15)}>
          <img src={region.image || '/api/uploads/hero_main.jpg'} alt={region.h1} width="1200" height="800" fetchPriority="high" className="w-full h-[300px] md:h-[440px] object-cover rounded-2xl shadow-2xl" />
          <div className="absolute -bottom-5 left-6 right-6 sm:right-auto bg-white rounded-xl px-5 py-4 shadow-xl border-l-4 border-[#066ce7]">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Kurze Wege aus Dresden</p>
            <p className="font-bold text-[#0F172A]">{region.name}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RegionServices({ region, services }) {
  const notes = region.service_notes || {};
  return (
    <section className="py-20 md:py-28 blueprint-bg" data-testid="region-services">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-12 max-w-2xl">Unsere Leistungen in {region.name}</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {services.map(s => (
            <Link key={s.key} to={servicePath(s)} className="group flex gap-5 bg-white border border-slate-200 rounded-2xl p-7 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-[#066ce7] hover:shadow-xl" data-testid={`region-service-${s.key}`}>
              <span className="w-12 h-12 rounded-xl bg-blue-50 text-[#066ce7] flex items-center justify-center shrink-0 transition-colors group-hover:bg-[#066ce7] group-hover:text-white">
                <ServiceIcon name={s.icon} className="w-6 h-6" />
              </span>
              <span className="flex-1">
                <span className="block text-xl font-bold text-[#0F172A]">{localized(s, 'title', 'de')}</span>
                <span className="block text-slate-600 text-sm leading-relaxed mt-2">{notes[s.key] || localized(s, 'description', 'de')}</span>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#066ce7] mt-4">Mehr erfahren <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function Highlights({ region }) {
  const items = region.highlights || [];
  if (!items.length) return null;
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="region-highlights">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-14">Gut zu wissen</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {items.map((h, i) => (
            <div key={i} className="pt-8 border-t-2 border-slate-100 hover:border-[#066ce7] transition-colors duration-300" data-testid={`region-highlight-${i}`}>
              <span className="font-mono text-sm text-[#066ce7] block mb-3">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="text-xl font-bold text-[#0F172A] mb-2">{h.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{h.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function References({ region }) {
  const refs = region.references || [];
  if (!refs.length) return null;
  return (
    <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-100" data-testid="region-references">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A]">Typische Projekte in der Region</h2>
          <p className="text-sm text-slate-500 max-w-sm">Beispiele aus unserem Leistungsspektrum – gern nennen wir Ihnen passende Referenzen im persönlichen Gespräch.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {refs.map((r, i) => (
            <article key={i} className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col transition-shadow duration-300 hover:shadow-xl" data-testid={`region-reference-${i}`}>
              <p className="text-xs font-semibold uppercase tracking-wider text-[#066ce7] mb-3">{r.services}</p>
              <h3 className="text-lg font-bold text-[#0F172A] mb-3">{r.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed flex-1">{r.text}</p>
              <div className="flex flex-wrap gap-4 text-xs text-slate-500 mt-6 pt-5 border-t border-slate-100">
                <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#066ce7]" aria-hidden="true" />{r.place}</span>
                <span className="inline-flex items-center gap-1.5"><Ruler className="w-3.5 h-3.5 text-[#066ce7]" aria-hidden="true" />{r.size}</span>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Area({ region }) {
  const zoom = /\d/.test(region.map_query || '') ? 13 : 7;
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="region-area">
      <div className="max-w-7xl mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4">Einsatzorte (Auswahl)</h2>
          <p className="text-slate-600 leading-relaxed mb-8">Wir sind in der gesamten Region für Sie im Einsatz – unter anderem in:</p>
          <ul className="flex flex-wrap gap-2" data-testid="region-cities">
            {(region.cities || []).map(c => (
              <li key={c} className="text-sm font-medium text-[#0F172A] bg-slate-100 border border-slate-200 rounded-full px-4 py-1.5">{c}</li>
            ))}
          </ul>
          <p className="flex items-center gap-2 text-sm text-slate-500 mt-8"><Wrench className="w-4 h-4 text-[#066ce7]" aria-hidden="true" /> Montage, Wartung und Termine vor Ort mit eigenem Team.</p>
        </div>
        <GoogleMap query={region.map_query || region.name} zoom={zoom} title={`Karte: ${region.name}`} className="h-[320px] md:h-[400px]" testid="region-map" />
      </div>
    </section>
  );
}

function OtherRegions({ region, regions }) {
  const others = regions.filter(r => r.key !== region.key);
  if (!others.length) return null;
  return (
    <section className="py-16 bg-white border-t border-slate-100" data-testid="region-others">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row md:items-center gap-6">
        <h2 className="text-lg font-bold text-[#0F172A] shrink-0">Weitere Einsatzgebiete</h2>
        <div className="flex flex-wrap gap-3">
          {others.map(r => (
            <Link key={r.key} to={regionPath(r)} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-slate-200 rounded-full px-4 py-2 text-[#0F172A] hover:border-[#066ce7] hover:text-[#066ce7] transition-colors" data-testid={`region-other-${r.key}`}>
              {r.name} <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function RegionPage() {
  const { slug } = useParams();
  const region = useRegion(slug);
  const services = useServices();
  const regions = useRegions();

  if (region === null) return <NotFoundPage />;
  if (!region) return <div className="min-h-[70vh] bg-[#0F172A]" data-testid="region-loading"><SEO /></div>;

  return (
    <div data-testid="region-page">
      <SEO />
      <Hero region={region} />
      <RegionServices region={region} services={services} />
      <Highlights region={region} />
      <References region={region} />
      <Area region={region} />
      <FaqSection faqs={region.faqs || []} testid="region-faq" />
      <OtherRegions region={region} regions={regions} />
      <Cta testid="region-cta" />
    </div>
  );
}
